import type { AgentRef } from "@/types/agent-summary"
import type {
  Agent,
  AgentPacksResponse,
  AgentResponse,
  AgentRunHistoryResponse,
  AgentsQuery,
  AgentsResponse,
  RunAgentNowResponse,
} from "@/types/agents"

import { baseApi } from "./base-api"

export const AGENT_RUNS_PER_PAGE = 10

// Home's agents: every price alert and custom agent as one list (GET
// /api/agents), plus the few operations Home offers on one. Creation stays
// with each type's own flow.
export const agentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<AgentsResponse, AgentsQuery | void>({
      query: (params) => ({
        url: "/api/agents",
        params: params ?? undefined,
      }),
      providesTags: ["Agent"],
    }),
    // The same list a page at a time, for surfaces that show all of it.
    getAgentList: builder.infiniteQuery<
      AgentsResponse,
      Omit<AgentsQuery, "page">,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.meta.next_page ?? undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/api/agents",
        params: pageParam > 1 ? { ...queryArg, page: pageParam } : queryArg,
      }),
      providesTags: ["Agent"],
    }),
    // The composer's tool packs, for the edit dialog's "what it has access to".
    getAgentPacks: builder.query<AgentPacksResponse["packs"], void>({
      query: () => ({ url: "/api/agents/packs" }),
      transformResponse: (response: AgentPacksResponse) => response.packs,
    }),
    getAgent: builder.query<Agent, AgentRef>({
      query: (ref) => ({ url: `/api/agents/${ref}` }),
      transformResponse: (response: AgentResponse) => response.agent,
      providesTags: ["Agent"],
    }),
    getAgentRunHistory: builder.query<AgentRunHistoryResponse, AgentRef>({
      query: (ref) => ({
        url: `/api/agents/${ref}/runs`,
        params: { per_page: AGENT_RUNS_PER_PAGE },
      }),
      providesTags: ["AgentRuns"],
    }),
    updateAgentStatus: builder.mutation<
      Agent,
      { ref: AgentRef; status: "active" | "paused" }
    >({
      query: ({ ref, status }) => ({
        url: `/api/agents/${ref}`,
        method: "PATCH",
        data: { status },
      }),
      transformResponse: (response: AgentResponse) => response.agent,
      invalidatesTags: ["Agent"],
    }),
    deleteAgent: builder.mutation<void, AgentRef>({
      query: (ref) => ({
        url: `/api/agents/${ref}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Agent", "AgentRuns"],
    }),
    runAgentNow: builder.mutation<RunAgentNowResponse, AgentRef>({
      query: (ref) => ({
        url: `/api/agents/${ref}/run_now`,
        method: "POST",
      }),
      invalidatesTags: ["Agent", "AgentRuns"],
    }),
  }),
})

export const {
  useGetAgentsQuery,
  useGetAgentListInfiniteQuery,
  useGetAgentPacksQuery,
  useGetAgentQuery,
  useLazyGetAgentQuery,
  useGetAgentRunHistoryQuery,
  useUpdateAgentStatusMutation,
  useDeleteAgentMutation,
  useRunAgentNowMutation,
} = agentsApi
